import React, { useMemo, useState } from "react";
import { Card, CardBody, Container, Button, Spinner } from "reactstrap";
import tableStyles from "../../../../assets/scss/common/table.module.scss";
import CommonSearch from "../../../../Components/Common/CommonSearch";
import { perPageData, showToastMessage } from "common/service/CommonService";
import CommonTableIndexWise from "Components/Common/CommonTableIndexWise";
import AddEditUserAssignment from "../components/AddEditUserAssignment";
import AddUserModal from "../components/AddUserModal";
import Reason from "common/utilities/Reason/Reason";
import DeleteModal from "Components/Common/DeleteModal";

import {
  PERMISSION_ACTIONS,
  PERMISSION_MODULES,
} from "../../../../common/constants/PermissionConstants";
import CommonButton from "../../../../Components/Common/CommonButton";
import { userList } from "../helpers/userconstant";
import { queryClient } from "api/queryClient";
import { useUserAssignmentService } from "modules/Library/Users/api/useUserAssignmentQuery";
import FilePreviewModal from "../../../Study/Attachments/components/FilePreviewModal";
import { useAuditService } from "modules/Audit/api/useAuditQuery";
import { useRoleService } from "modules/Library/Role/api/useRoleQuery";
import { Assignment, RoleOption, UserAssigned, UserAssignmentFormValues } from "../helpers/user.types";

/* ================= COMPONENT ================= */

const UserList: React.FC = () => {
  document.title = "Users | ZENTIXS VISTA";

  //-------------------------------------------------------------------------------------------
  //--> State
  //-------------------------------------------------------------------------------------------

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [searchText, setSearchText] = useState<string | null>(null);
  const [currentPageNumber, setCurrentPageNumber] = useState(1);
  const [selectedRecord, setSelectedRecord] =
    useState<Partial<UserAssigned> | null>(null);
  const [editRecord, setEditRecord] =
    useState<Partial<UserAssignmentFormValues> | null>(null);
  const [modalMode, setModalMode] = useState<"add" | "edit" | "assign">("add");
  const [updatedRecord, setUpdatedRecord] = useState<Assignment | null>(null);
  const [isReasonModal, setIsReasonModal] = useState(false);
  const [showDeleteReason, setShowDeleteReason] = useState<boolean>(false);
  const [addUserModalMode, setAddUserModalMode] = useState<"add" | "edit">("add");
  const [selectedUserForEdit, setSelectedUserForEdit] = useState<any>(null);
  const [pendingUpdateData, setPendingUpdateData] = useState<any>(null);

  //Audit
  const [isPreviewOpen, setIsPreviewOpen] = useState<boolean>(false);
  const [previewFile, setPreviewFile] = useState<File | null>(null);
  const { SelectAllMutation: AuditMutation } = useAuditService();

  const inEditMode = Boolean(editRecord);

  //-------------------------------------------------------------------------------------------
  //--> Toggles
  //-------------------------------------------------------------------------------------------

  const toggleModal = () => setIsModalOpen((p) => !p);
  const toggleAddUserModal = () => setIsAddUserModalOpen((p) => !p);
  const toggleDeleteModal = () => {
    setDeleteModal((p) => !p);
    setShowDeleteReason(false);
  };
  const toggleReasonModal = () => setIsReasonModal((p) => !p);
  const toggleDeleteReason = () => setShowDeleteReason((p) => !p);

  const openAddUserModal = () => {
    setAddUserModalMode("add");
    setSelectedUserForEdit(null);
    toggleAddUserModal();
  };

  //-------------------------------------------------------------------------------------------
  //--> Query Services
  //-------------------------------------------------------------------------------------------

  const {
    SelectAllQuery,
    InsertMutation,
    InsertUserMutation,
    UpdateMutation,
    DeleteMutation,
    RoleDropdownQuery,
    UserDropdownQuery,
  } = useUserAssignmentService({
    searchText: searchText,
    pageNumber: currentPageNumber,
    pageSize: perPageData,
    isselectallenabled: true
  });

  // const { data: ROLEList } = UserDropdownQuery;
  // const { data: ROLEList } = RoleDropdownQuery;
  const { DropdownQuery } = useRoleService({ getDropdown: true });
  const { data: ROLEList } = DropdownQuery;


  //-------------------------------------------------------------------------------------------
  //--> Dropdown Options
  //-------------------------------------------------------------------------------------------

  const roleDropdownOptions: RoleOption[] = useMemo(
    () =>
      (ROLEList?.data ?? []).map((r: any) => ({
        value: r.roleid,
        label: r.title,
      })),
    [ROLEList],
  );

  //-------------------------------------------------------------------------------------------
  //--> Table Data
  //-------------------------------------------------------------------------------------------

  const { data: userResponse, isLoading: siteLoading } = SelectAllQuery as {
    data?: { data?: UserAssigned[] };
    isLoading: boolean;
  };

  const userAssignmentData: UserAssigned[] = useMemo(
    () =>
      (userResponse?.data ?? []).map((s) => ({
        ...s,
      })),
    [userResponse],
  );

  const assignmentUserData: any = useMemo(
    () =>
      userAssignmentData.map((s) => ({
        userid: s.userid,
        username: s.username,
        useremail: s.useremail,
        roleid: s.roleid,
        userassignmentid: s.userassignmentid ?? undefined,
      })),
    [userAssignmentData],
  );

  //-------------------------------------------------------------------------------------------
  //--> Search
  //-------------------------------------------------------------------------------------------

  const onSearch = (text: string | null) => {
    setSearchText(text);
    setCurrentPageNumber(1);
  };

  //-------------------------------------------------------------------------------------------
  //--> Save (UPDATE)
  //-------------------------------------------------------------------------------------------

  const onSave = ({ assignments }: UserAssignmentFormValues) => {
    const assignmentToInsert = assignments?.[0];
    if (!assignmentToInsert) return;

    const payload = {
      userid: assignmentToInsert.userid,
      username: assignmentToInsert.username,
      roleid: assignmentToInsert.roleid,
    };

    InsertMutation.mutate(payload, {
      onSuccess: (res: any) => {
        queryClient.invalidateQueries({
          queryKey: ["userassignment-selectAll"],
        });

        if (res?.data === -1 || res?.statuscode === -1 || res?.isexist === -1) {
          showToastMessage("error", res?.message);
          return;
        }
        toggleModal()
        showToastMessage("success", res?.message);
      },
      onError: (err: any) => {
        // Handle direct string errors from interceptor or object-based errors
        const errorMessage =
          typeof err === "string" ? err : (err?.message ?? "An error occurred");
        showToastMessage("error", errorMessage);
      },
    });
  };

  const handleSaveNewUser = (data: any) => {
    if (addUserModalMode === "edit") {
      setPendingUpdateData(data);
      setIsReasonModal(true);
    } else {
      InsertUserMutation.mutate(data, {
        onSuccess: (res: any) => {
          queryClient.invalidateQueries({
            queryKey: ["userassignment-selectAll"],
          });


          if (res?.data === -1 || res?.statuscode === -1 || res?.isexist === -1) {
            showToastMessage("error", res?.message);
            return;
          }

          showToastMessage("success", res?.message);
          setCurrentPageNumber(1);
          toggleAddUserModal();
          setSearchText("")
        },
        onError: (err: any) => {
          const errorMessage =
            typeof err === "string" ? err : (err?.message ?? "An error occurred");
          showToastMessage("error", errorMessage);
        },
      });
    }
  };

  //-------------------------------------------------------------------------------------------
  //--> Edit
  //-------------------------------------------------------------------------------------------

  const siteEdit = (row: UserAssigned) => {
    setAddUserModalMode("edit");
    setSelectedUserForEdit(row);
    toggleAddUserModal();

  };

  const siteAssignRole = (row: UserAssigned) => {
    setEditRecord({
      assignments: assignmentUserData.filter(
        (a: any) => a.userid === row.userid,
      ),
    });
    setModalMode("assign");
    toggleModal();
  };

  //-------------------------------------------------------------------------------------------
  //--> Update with Reason
  //-------------------------------------------------------------------------------------------

  const onUpdateClick = (data: UserAssignmentFormValues) => {
    const assignmentToUpdate = data.assignments[0];
    if (!assignmentToUpdate) return;
    setUpdatedRecord(assignmentToUpdate);
    setIsReasonModal(true);
  };

  const btnReasonClick = (reasonPayload: {
    id: number | null;
    reason: string;
  }) => {
    if (pendingUpdateData) {
      const payload = {
        userid: selectedUserForEdit?.userid,
        username: pendingUpdateData.username,
        firstname: pendingUpdateData.firstname,
        lastname: pendingUpdateData.lastname,
        userpassword: pendingUpdateData.userpassword || null,
        useremail: pendingUpdateData.useremail,
        contactnumber: pendingUpdateData.contactnumber,
        updatereason: reasonPayload.reason,
      };

      UpdateMutation.mutate(payload, {
        onSuccess: (res: any) => {
          queryClient.invalidateQueries({
            queryKey: ["userassignment-selectAll"],
          });

          if (res?.data === -1 || res?.statuscode === -1 || res?.isexist === -1) {
            showToastMessage("error", res?.message);
            return;
          }
          showToastMessage("success", res?.message);
          toggleReasonModal();
          setPendingUpdateData(null);
          setSelectedUserForEdit(null);
          setCurrentPageNumber(1);
          setSearchText("")
          toggleAddUserModal();
        },
        onError: (err: any) => {
          const errorMessage =
            typeof err === "string" ? err : (err?.message ?? "An error occurred");
          showToastMessage("error", errorMessage);
        },
      });
      return;
    }

    if (!updatedRecord) return;

    const payload = {
      userassignmentid: reasonPayload.id,
      userid: updatedRecord.userid,
      roleid: updatedRecord.roleid,
      updatereason: reasonPayload.reason,
    };

    UpdateMutation.mutate(payload, {
      onSuccess: (res: any) => {
        if (res?.data === -1 || res?.statuscode === -1) {
          showToastMessage("error", "User not found.");
          return;
        }
        queryClient.invalidateQueries({
          queryKey: ["userassignment-selectAll"],
        });
        showToastMessage("success", res?.message);
        toggleReasonModal();
        setUpdatedRecord(null);
        setSearchText("")
        setCurrentPageNumber(1);
      },
    });
  };

  //-------------------------------------------------------------------------------------------
  //--> Delete
  //-------------------------------------------------------------------------------------------

  const handleDeleteClick = (item: Partial<UserAssigned>) => {
    setSelectedRecord(item);
    toggleDeleteModal();
  };

  const confirmDelete = (reasonPayload: {
    id: number | null;
    reason: string;
  }) => {
    if (!selectedRecord) return;

    DeleteMutation.mutate(
      { userid: reasonPayload.id, updatereason: reasonPayload.reason },
      {
        onSuccess: (res: any) => {
          if (res?.data === -1 || res?.statuscode === -1) {
            showToastMessage("error", "User not found.");
            return;
          }
          queryClient.invalidateQueries({
            queryKey: ["userassignment-selectAll"],
          });


          const totalRecords = res.data[0]?.total_count || 0;
          const updatedTotalRecords = totalRecords - 1;
          const lastPage = Math.ceil(updatedTotalRecords / perPageData);
          const updatedPage =
            lastPage === 0 ? 1 : Math.min(currentPageNumber, lastPage);
          setCurrentPageNumber(updatedPage);

          showToastMessage("success", res?.message);
          setSelectedRecord(null);
          toggleDeleteModal();
          toggleDeleteReason();
          setSearchText("")
        },
        onError: (err: any) => {
          showToastMessage("error", err);
        },
      },
    );
  };

  const onAuditClick = () => {
    const payload = {
      modulename: "User",
    };

    AuditMutation.mutate(payload, {
      onSuccess: (data: any) => {
        if (data instanceof Blob) {
          const pdfFile = new File([data], "User_Audit_Log.pdf", {
            type: "application/pdf",
          });
          setPreviewFile(pdfFile);
          setIsPreviewOpen(true);
        }
      },
      onError: (error: any) => {
        showToastMessage("error", "Error fetching audit logs");
        console.error("Error fetching audit logs:", error);
      },
    });
  };

  //-------------------------------------------------------------------------------------------
  //--> Render
  //-------------------------------------------------------------------------------------------

  return (
    <div className="page-content">
      <Container fluid className="px-0">
        <Card className={tableStyles.card}>
          <CardBody>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <CommonSearch onSearch={onSearch} searchText={searchText} />
              <div className="d-flex gap-2">
                <CommonButton
                  module={PERMISSION_MODULES.USER}
                  action={PERMISSION_ACTIONS.AUDIT}
                  color="primary"
                  size="sm"
                  onClick={onAuditClick}
                  disabled={AuditMutation.isPending}
                  outline
                >
                  {AuditMutation.isPending ? (
                    <Spinner size="sm" className="me-2" />
                  ) : (
                    <i className="ri-history-line me-1"></i>
                  )}
                  Audit
                </CommonButton>
                <CommonButton
                  color="primary"
                  outline
                  onClick={openAddUserModal}
                  size="sm"
                  module={PERMISSION_MODULES.USER}
                  action={PERMISSION_ACTIONS.ADD}
                >
                  <i className="ri-add-line me-1" /> Add
                </CommonButton>
              </div>
            </div>

            <CommonTableIndexWise
              moduleName={PERMISSION_MODULES.USER}
              columns={userList}
              data={userAssignmentData}
              loading={siteLoading || SelectAllQuery.isFetching}
              pageSize={perPageData}
              currentPage={currentPageNumber}
              onPageChange={setCurrentPageNumber}
              totalRecords={userAssignmentData?.[0]?.total_count || 0}
              actions={{
                onEdit: siteEdit,
                onAssignRole: siteAssignRole,
                onDelete: handleDeleteClick,
              }}
              showStatus={false}
            />
          </CardBody>
        </Card>

        {/* Add / Edit Modal */}
        <AddEditUserAssignment
          isOpen={isModalOpen}
          toggle={toggleModal}
          mode={modalMode}
          existingData={editRecord || undefined}
          onSave={modalMode === "edit" ? onUpdateClick : onSave}
          onCancel={toggleModal}
          userAssignmentData={assignmentUserData}
          roleDropdownData={roleDropdownOptions}
          loading={
            UpdateMutation.isPending ||
            InsertMutation.isPending
          }
        />

        {/* Add User Modal */}
        <AddUserModal
          isOpen={isAddUserModalOpen}
          toggle={toggleAddUserModal}
          onSave={handleSaveNewUser}
          loading={InsertUserMutation.isPending}
          mode={addUserModalMode}
          existingData={selectedUserForEdit}
        />

        {/* Reason Modal */}
        {isReasonModal && (
          <Reason
            isOpen={isReasonModal}
            toggle={toggleReasonModal}
            action="Update"
            id={pendingUpdateData ? (selectedUserForEdit?.userid ?? null) : (updatedRecord?.userassignmentid ?? null)}
            click={btnReasonClick}
            isLoading={UpdateMutation.isPending}
          />
        )}

        {/* Delete Modal */}
        {deleteModal && (
          <DeleteModal
            isopen={deleteModal}
            toggle={toggleDeleteModal}
            recordId={selectedRecord?.userid ?? 0}
            recordName={selectedRecord?.username}
            onDeleteClick={confirmDelete}
            showDeleteReason={showDeleteReason}
            toggleDeleteReason={toggleDeleteReason}
            isLoading={DeleteMutation.isPending}
          />
        )}
      </Container>

      {/* File Preview Modal */}
      {isPreviewOpen && (
        <FilePreviewModal
          file={previewFile}
          isOpen={isPreviewOpen}
          size={"xl"}
          onClose={() => {
            setIsPreviewOpen(false);
            setPreviewFile(null);
          }}
        />
      )}
    </div>
  );
};

export default UserList;